<template>
  <div class="page">
    <div class="toolbar">
      <el-input
        v-model="filters.name"
        placeholder="租户名称"
        clearable
        class="mr8"
      />
      <el-input
        v-model="filters.subname"
        placeholder="子域名"
        clearable
        class="mr8"
      />
      <el-select
        v-model="filters.is_active"
        placeholder="状态"
        clearable
        class="mr8"
        style="width: 140px"
      >
        <el-option :value="true" label="启用" />
        <el-option :value="false" label="停用" />
      </el-select>
      <el-button type="primary" @click="loadTenants">查询</el-button>
      <el-button class="ml8" @click="resetFilters">重置</el-button>
      <div class="flex1"></div>
      <el-button type="success" @click="openCreate">新建租户</el-button>
    </div>

    <el-table :data="items" v-loading="loading" size="small" border>
      <el-table-column prop="name" label="名称" min-width="160" />
      <el-table-column prop="subname" label="子域名" min-width="140" />
      <el-table-column prop="is_active" label="状态" width="90">
        <template #default="scope">
          <el-tag :type="scope.row.is_active ? 'success' : 'info'">{{
            scope.row.is_active ? "启用" : "停用"
          }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="api_enabled" label="API" width="90">
        <template #default="scope">
          <el-tag :type="scope.row.api_enabled ? 'success' : 'info'">{{
            scope.row.api_enabled ? "开启" : "关闭"
          }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="api_rate_limit" label="限流(次/分)" width="120" />
      <el-table-column prop="max_devices" label="最大设备数" width="120" />
      <el-table-column label="操作" width="220" fixed="right">
        <template #default="scope">
          <el-button size="small" @click="openEdit(scope.row)">编辑</el-button>
          <el-popconfirm
            title="确认删除该租户？"
            @confirm="removeTenant(scope.row)"
          >
            <template #reference>
              <el-button size="small" type="danger">删除</el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager">
      <el-pagination
        background
        layout="prev, pager, next, jumper, total"
        :total="total"
        :page-size="query.page_size"
        :current-page="query.page"
        @current-change="(p:number)=>{query.page=p; loadTenants()}"
      />
    </div>

    <el-dialog
      v-model="dialog.visible"
      :title="dialog.isEdit ? '编辑租户' : '新建租户'"
      width="560px"
    >
      <el-form :model="form" label-width="100px">
        <el-form-item label="名称">
          <el-input v-model="form.name" />
        </el-form-item>
        <el-form-item label="子域名">
          <el-input v-model="form.subname" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.description" type="textarea" />
        </el-form-item>
        <el-form-item label="联系人">
          <el-input v-model="form.contact_name" />
        </el-form-item>
        <el-form-item label="邮箱">
          <el-input v-model="form.contact_email" />
        </el-form-item>
        <el-form-item label="电话">
          <el-input v-model="form.contact_phone" />
        </el-form-item>
        <el-form-item label="最大设备数">
          <el-input-number v-model="form.max_devices" :min="0" />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.is_active" />
        </el-form-item>
        <el-form-item label="API启用">
          <el-switch v-model="form.api_enabled" />
        </el-form-item>
        <el-form-item label="API限流">
          <el-input-number v-model="form.api_rate_limit" :min="0" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialog.visible = false">取消</el-button>
        <el-button type="primary" @click="submit">提交</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted } from "vue";
import { ElMessage } from "element-plus";
import { TenantServiceApi } from "@/services/dataService";

const loading = ref(false);
const items = ref<any[]>([]);
const total = ref(0);
const query = reactive({ page: 1, page_size: 10 });
const filters = reactive<{
  name: string | undefined;
  subname: string | undefined;
  is_active: boolean | undefined;
}>({ name: undefined, subname: undefined, is_active: undefined });

const dialog = reactive({
  visible: false,
  isEdit: false,
  editingId: null as null | string,
});
const form = reactive<any>({
  name: "",
  subname: "",
  description: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  max_devices: 10000,
  is_active: true,
  api_enabled: false,
  api_rate_limit: 100,
});

const loadTenants = async () => {
  loading.value = true;
  try {
    const params: any = { page: query.page, page_size: query.page_size };
    if (filters.name) params.name = filters.name;
    if (filters.subname) params.subname = filters.subname;
    if (filters.is_active !== undefined) params.is_active = filters.is_active;
    const resp = await TenantServiceApi.listTenants(params);
    const data = resp?.data?.data;
    items.value = data?.items || [];
    total.value = data?.total || 0;
  } catch (e: any) {
    ElMessage.error(e?.message || "加载失败");
  } finally {
    loading.value = false;
  }
};

const resetFilters = () => {
  filters.name = undefined;
  filters.subname = undefined;
  filters.is_active = undefined;
};

const openCreate = () => {
  dialog.visible = true;
  dialog.isEdit = false;
  dialog.editingId = null;
  Object.assign(form, {
    name: "",
    subname: "",
    description: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    max_devices: 10000,
    is_active: true,
    api_enabled: false,
    api_rate_limit: 100,
  });
};

const openEdit = (row: any) => {
  dialog.visible = true;
  dialog.isEdit = true;
  dialog.editingId = row.id;
  Object.assign(form, { ...row });
};

const submit = async () => {
  try {
    const payload: any = {
      name: form.name,
      subname: form.subname,
      description: form.description,
      contact_name: form.contact_name,
      contact_email: form.contact_email,
      contact_phone: form.contact_phone,
      max_devices: form.max_devices,
      is_active: form.is_active,
      api_enabled: form.api_enabled,
      api_rate_limit: form.api_rate_limit,
    };
    if (dialog.isEdit && dialog.editingId) {
      const resp = await TenantServiceApi.updateTenant(
        dialog.editingId,
        payload
      );
      if (resp?.data?.code === 0) {
        ElMessage.success("已更新");
        dialog.visible = false;
        loadTenants();
        return;
      }
    } else {
      const resp = await TenantServiceApi.createTenant(payload);
      if (resp?.data?.code === 0) {
        ElMessage.success("已创建");
        dialog.visible = false;
        loadTenants();
        return;
      }
    }
    ElMessage.error("提交失败");
  } catch (e: any) {
    ElMessage.error(e?.message || "提交失败");
  }
};

const removeTenant = async (row: any) => {
  try {
    const resp = await TenantServiceApi.deleteTenant(row.id);
    if (resp?.data?.code === 0) {
      ElMessage.success("已删除");
      loadTenants();
    } else {
      ElMessage.error(resp?.data?.message || "删除失败");
    }
  } catch (e: any) {
    ElMessage.error(e?.message || "删除失败");
  }
};

onMounted(loadTenants);
</script>

<style scoped>
.page {
  padding: 16px;
}
.toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}
.flex1 {
  flex: 1;
}
.mr8 {
  margin-right: 8px;
}
.ml8 {
  margin-left: 8px;
}
.pager {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}
</style>
